import React, { useEffect, useState } from "react"
import styles from "./EditBookingPage.module.css"
import { useCreatePostApiMutation, useUpdateBookingsMutation, useGetAcceptedGeneratedPostsAllQuery } from "../../features/OverViewAuth/overViewApi"
import { checkBookingConflict } from "../../utils/bookingValidation"
import { toast } from "react-toastify"
import { FiPhone } from "react-icons/fi"
import { MdOutlineMail } from "react-icons/md"
import { FaEdit } from "react-icons/fa"
import { Button } from "react-bootstrap"
import { useAppSelector } from "../../app/hooks"
import { useNavigate } from "react-router-dom"
import dummyImage from "../../images/dummy-image.jpg"
import moment from "moment"
import CompleteProfileModal from "../Modal/CompleteProfile"
import { Tooltip, Typography } from "@mui/material"
import { BsInfoCircleFill } from "react-icons/bs"
import { createLocalDateTime, getLocalDateString, formatToLocalWithOffset } from "../../utils/LocalDate"

export default function EditBookingPage() {
  const [showProfileModal, setShowProfileModal] = React.useState(false)
  const bookingDetails = useAppSelector(
    (state) => state.postCreate.bookingDetails,
  )

  const { user } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()

  const hasPhoneNumber = () => {
    return user?.contactNo?.trim()?.length > 0;
  }

  useEffect(() => {
    if (!hasPhoneNumber()) {
      setShowProfileModal(true)
    }
  }, [])

  function formatAgeRange(ageRange: any) {
    const [minAge, maxAge] = ageRange.split(",")
    return `${minAge} - ${maxAge}`
  }

  const [postCreate, { isLoading }] = useCreatePostApiMutation();
  const [updateBooking, { isLoading: isUpdating }] = useUpdateBookingsMutation();
  const { data: existingBookingsData } = useGetAcceptedGeneratedPostsAllQuery({});

  const handleNext = async () => {
    if (!hasPhoneNumber()) {
      setShowProfileModal(true);
      return;
    }

    try {
      // Get local bookings from session storage
      const saved = sessionStorage.getItem('booking_calendar_items');
      const localBookings = saved ? JSON.parse(saved) : [];

      // Merge backend and local bookings
      const allBookings = [
        ...(existingBookingsData?.data || []),
        ...localBookings
      ];

      // Handle Multiple Bookings (Date Range)
      if ((bookingDetails as any)?.dateRange && !(bookingDetails as any)._id) { // Only for new bookings
        const { fromDate, toDate } = (bookingDetails as any).dateRange;
        const [fYear, fMonth, fDay] = fromDate.split('-').map(Number);
        const [tYear, tMonth, tDay] = toDate.split('-').map(Number);
        const start = new Date(fYear, fMonth - 1, fDay);
        const end = new Date(tYear, tMonth - 1, tDay);

        // Loop through dates
        let currentDate = new Date(start);
        const promises = [];
        const dateList: any[] = [];

        while (currentDate <= end) {
          const dateStr = getLocalDateString(currentDate);
          dateList.push(dateStr);

          // Calculate From/Till for this specific date
          const localDateTime = createLocalDateTime(dateStr, bookingDetails?.timeUpdate);
          const utcString = formatToLocalWithOffset(localDateTime);

          // Validation: Check for duplicates
          const hasConflict = checkBookingConflict(
            {
              serviceId: bookingDetails?.service,
              from: utcString,
            },
            allBookings
          );

          if (hasConflict) {
            toast.error(`Slot already booked for ${dateStr} at ${bookingDetails?.timeUpdate}`);
            continue; // Skip this date
          }

          // Calculate end time
          const endDateTime = new Date(localDateTime);
          const hoursToSend = bookingDetails?.hours || 2;
          const hoursInMs = Number(hoursToSend) * 60 * 60 * 1000;
          endDateTime.setTime(localDateTime.getTime() + hoursInMs);
          const endUtcString = formatToLocalWithOffset(endDateTime);

          const dayPayload = new FormData();
          // Common fields
          dayPayload.append("location", bookingDetails?.location);
          dayPayload.append("service", bookingDetails?.service as any);
          dayPayload.append("serviceName", bookingDetails?.serviceName as any);
          dayPayload.append("ageRange", bookingDetails?.ageRange?.toString());
          dayPayload.append("description", bookingDetails?.description);
          dayPayload.append("personRequired", bookingDetails?.personRequired?.toString());
          dayPayload.append("language", bookingDetails?.language as any);
          dayPayload.append("extraDetails", JSON.stringify(bookingDetails?.extraDetails || {})); // Note: dateRange is in extraDetails/top-level but backend might not need it for single bookings

          // Specific Time fields
          dayPayload.append("from", utcString);
          dayPayload.append("till", endUtcString);
          dayPayload.append("hours", hoursToSend.toString());

          bookingDetails?.attachment?.forEach((image: any, index: number) => {
            const key = index === 0 ? "attachment" : `attachment${index}`
            dayPayload.append(key, image as any)
          })

          promises.push(postCreate(dayPayload));

          currentDate.setDate(currentDate.getDate() + 1);
        }

        const responses = await Promise.all(promises);

        // Check if all success
        const allSuccess = responses.every((res: any) => res?.data?.status === 200);

        if (allSuccess) {
          toast.success(`Successfully created ${promises.length} ${promises.length === 1 ? 'booking' : 'bookings'}`);

          // Map responses to booking objects with IDs so the calendar can use them
          const createdBookings = responses.map((res: any, index: number) => {
            const createdPost = res.data.data.createdPost;
            return {
              ...createdPost,
              // Ensure we have the exact date we used for creation
              date: dateList[index],
              // Map service info if returned object is different structure
              serviceId: createdPost.service?._id || bookingDetails?.service,
              serviceName: createdPost.service?.name || bookingDetails?.serviceName,
              // Original details needed for UI
              location: bookingDetails?.location,
              description: bookingDetails?.description,
              timeUpdate: bookingDetails?.timeUpdate,
              updateEndTime: bookingDetails?.updateEndTime,
              hours: bookingDetails?.hours,
              attachment: bookingDetails?.attachment
            };
          });

          // pass bookings back to calendar so it can display them immediately
          navigate(`/dashboard/create-booking/${bookingDetails?.service || 'view'}?serviceId=${bookingDetails?.service}&serviceName=${encodeURIComponent(bookingDetails?.serviceName)}`, {
            state: {
              range: (bookingDetails as any).dateRange,
              bookingDetails: bookingDetails, // pass base details if needed
              bookingsCreated: true,
              createdBookings: createdBookings // Pass the actual created objects
            }
          });
        } else {
          toast.error("Some bookings failed to create");
          // Navigate anyway?
          navigate(`/dashboard/create-booking/${bookingDetails?.service || 'view'}?serviceId=${bookingDetails?.service}&serviceName=${encodeURIComponent(bookingDetails?.serviceName)}`, {
            state: {
              range: (bookingDetails as any).dateRange
            }
          });
        }
        return;
      }

      // Format from and till for the payload to ensure they have the correct offset
      const fromISO = bookingDetails.from ? formatToLocalWithOffset(new Date(bookingDetails.from)) : "";
      const tillISO = bookingDetails.till ? formatToLocalWithOffset(new Date(bookingDetails.till)) : "";

      // Validation: Check for duplicates for single booking
      const hasConflict = checkBookingConflict(
        {
          _id: (bookingDetails as any)._id,
          serviceId: bookingDetails.service,
          from: fromISO,
        },
        allBookings
      );

      if (hasConflict) {
        toast.error("Slot already booked.");
        return;
      }

      const payload = new FormData()
      payload.append("location", bookingDetails?.location)
      payload.append("service", bookingDetails?.service as any)
      payload.append("serviceName", bookingDetails?.serviceName as any)
      payload.append("ageRange", bookingDetails?.ageRange?.toString())
      payload.append("from", fromISO)
      payload.append("till", tillISO)
      payload.append("hours", bookingDetails?.hours?.toString() || "2")
      payload.append("description", bookingDetails?.description)
      payload.append("personRequired", bookingDetails?.personRequired?.toString())
      payload.append("language", bookingDetails?.language as any)
      payload.append("extraDetails", JSON.stringify(bookingDetails?.extraDetails || {}))
      bookingDetails?.attachment?.forEach((image: any, index: number) => {
        const key = index === 0 ? "attachment" : `attachment${index}`
        payload.append(key, image as any)
      })

      if (bookingDetails._id) {
        payload.append("postId", bookingDetails._id);
        const res: any = await updateBooking(payload)

        if (res?.data?.message) {
          toast.success("Booking updated successfully");

          // CRITICAL FIX: Create a clean object with ONLY this booking's data
          const updatedBookingData = {
            _id: res.data.data?.createdPost?._id || bookingDetails._id,
            serviceId: bookingDetails.service,
            serviceName: bookingDetails.serviceName,
            service: bookingDetails.service,
            location: bookingDetails.location,
            ageRange: bookingDetails.ageRange,
            from: bookingDetails.from,
            till: bookingDetails.till,
            description: bookingDetails.description,
            personRequired: bookingDetails.personRequired,
            language: bookingDetails.language,
            extraDetails: bookingDetails.extraDetails,
            attachment: bookingDetails.attachment,
            timeUpdate: bookingDetails.timeUpdate,
            updateEndTime: bookingDetails.updateEndTime,
            hours: bookingDetails.hours,
            // Add these fields for the calendar
            originalBookingId: bookingDetails._id, // Important for matching
            startTime: bookingDetails.timeUpdate,
            endTime: bookingDetails.updateEndTime,
            date: bookingDetails.from ? getLocalDateString(new Date(bookingDetails.from)) : getLocalDateString(new Date())
          };

          navigate(`/dashboard/create-booking/${bookingDetails?.service || 'view'}?serviceId=${bookingDetails?.service}&serviceName=${encodeURIComponent(bookingDetails?.serviceName)}`, {
            state: {
              bookingDetails: updatedBookingData, // Use the clean object
              originalBookingId: bookingDetails._id // Pass this separately for matching
            }
          });

        } else {
          toast.error(res?.error?.data?.message || "Failed to update booking");
        }

      } else {
        const response: any = await postCreate(payload)

        if (response?.data?.status === 200) {
          const createdPost = response?.data?.data?.createdPost;
          // Create clean object for new booking
          const newBookingData = {
            _id: createdPost._id,
            serviceId: bookingDetails.service,
            serviceName: bookingDetails.serviceName,
            service: bookingDetails.service,
            location: bookingDetails.location,
            ageRange: bookingDetails.ageRange,
            from: bookingDetails.from,
            till: bookingDetails.till,
            description: bookingDetails.description,
            personRequired: bookingDetails.personRequired,
            language: bookingDetails.language,
            extraDetails: bookingDetails.extraDetails,
            attachment: bookingDetails.attachment,
            timeUpdate: bookingDetails.timeUpdate,
            updateEndTime: bookingDetails.updateEndTime,
            hours: bookingDetails.hours,
            startTime: bookingDetails.timeUpdate,
            endTime: bookingDetails.updateEndTime,
            date: bookingDetails.from ? getLocalDateString(new Date(bookingDetails.from)) : getLocalDateString(new Date())
          };

          navigate(`/dashboard/create-booking/${bookingDetails?.service || 'view'}?serviceId=${bookingDetails?.service}&serviceName=${encodeURIComponent(bookingDetails?.serviceName)}`, {
            state: {
              bookingDetails: newBookingData
            }
          });
        } else {
          toast.error(response?.error?.data?.message || "Failed to create booking");
        }
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    }
  }

  const handleEdit = () => {
    const params = new URLSearchParams();

    // Add service info
    if (bookingDetails?.service) {
      params.append("serviceId", bookingDetails.service);
    }
    if (bookingDetails?.serviceName) {
      params.append("serviceName", bookingDetails.serviceName);
    }

    // Add date range or single date
    if (bookingDetails?.dateRange) {
      params.append("fromDate", bookingDetails.dateRange.fromDate);
      params.append("toDate", bookingDetails.dateRange.toDate);
    } else if (bookingDetails?.from) {
      // Extract date part directly from the ISO string (which acts as our "local" truth)
      // string is like "2025-12-26T00:00:00.000Z" -> "2025-12-26"
      const dateStr = getLocalDateString(new Date(bookingDetails.from));
      params.append("fromDate", dateStr);
      params.append("toDate", dateStr);
      params.append("date", dateStr);
    }

    // Add times
    if (bookingDetails?.timeUpdate) {
      params.append("fromTime", bookingDetails.timeUpdate);
    }
    if (bookingDetails?.updateEndTime) {
      params.append("toTime", bookingDetails.updateEndTime);
    }

    navigate(`/dashboard/booking-form/${bookingDetails?.service}?${params.toString()}`, {
      state: {
        bookingData: bookingDetails,
        from: 'calendar_edit'
      }
    });
  }

  const ageRange = bookingDetails?.ageRange

  // Function to render extra details
  const renderExtraDetails = () => {
    if (!bookingDetails?.extraDetails || Object.keys(bookingDetails.extraDetails).length === 0) {
      return null;
    }

    return (
      <div className={styles["edt-booking-extra-details"]}>
        <h4 className={styles["edt-booking-extra-title"]}>Additional Details</h4>
        {Object.entries(bookingDetails.extraDetails).map(([key, value]) => (
          <p key={key}>
            <span className={styles["edt-booking-label"]}>
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </span>{" "}
            - {Array.isArray(value) ? value.join(", ") : (value as React.ReactNode)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className={styles["edt-booking-maincard"]}>
        <div className={styles["edt-booking-card"]}>
          <div className={styles["edt-booking-header"]}>
            <img
              src={user?.profilePic || dummyImage}
              alt={`${user?.profilePic}`}
              className={styles["edt-booking-profile-image"]}
            />
            <div className={styles["edt-booking-info"]}>
              <h2 className={styles["edt-booking-name"]}>
                {user?.name}
              </h2>
              <Typography
                variant="body1"
                className={`${styles["edt-booking-contact"]} mb-2 d-inline-flex gap-1 align-items-center`}
              >
                <MdOutlineMail size={20} className={styles["edt-booking-icon"]} />
                <span className="text-truncate d-none d-md-inline-block" style={{ width: "180px" }}>
                  {user?.email}
                </span>
                <span className="text-truncate d-md-none" style={{ width: "80px" }}>
                  {user?.email}
                </span>
                <Tooltip
                  title={user?.email || "-----"}
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={8000}
                >
                  <Button
                    style={{ minWidth: "10px" }}
                    className="m-0 p-0 bg-white border-white"
                  >
                    <BsInfoCircleFill color="#F475A4" size={14} />
                  </Button>
                </Tooltip>
              </Typography>
              <p className={styles["edt-booking-contact"]}>
                <FiPhone size={18} className={styles["edt-booking-icon"]} />
                {user?.contactNo}
              </p>
            </div>
            <button
              className={styles["edt-booking-edit"]}
              onClick={handleEdit}
            >
              <FaEdit color="#F475A4" size={22} />
            </button>
          </div>
          <div className={styles["edt-booking-divider"]} />
          <div className={styles["edt-booking-details"]}>
            <p>
              <span className={styles["edt-booking-label"]}>
                Service Required
              </span>{" "}
              - <span>{bookingDetails?.serviceName}</span>
            </p>
            <p>
              <span className={styles["edt-booking-label"]}>Date & Time</span> -
              {(bookingDetails as any)?.dateRange ? (
                <>
                  {moment((bookingDetails as any).dateRange.fromDate).format(" ddd DD MMM YYYY")} to {moment((bookingDetails as any).dateRange.toDate).format(" ddd DD MMM YYYY")}
                </>
              ) : (
                moment.utc(bookingDetails?.from).local().format(" ddd DD  MMMM  YYYY")
              )} |{" "}
              {bookingDetails?.from && bookingDetails?.till ? (
                <>
                  {moment.utc(bookingDetails.from).local().format("h:mm A")} - {moment.utc(bookingDetails.till).local().format("h:mm A")}
                </>
              ) : (
                bookingDetails?.timeUpdate + " - " + bookingDetails?.updateEndTime
              )}
            </p>
            <p>
              <span className={styles["edt-booking-label"]}>
                Number Of Providers Required
              </span>{" "}
              - {bookingDetails?.personRequired}
            </p>
            <p>
              <span className={styles["edt-booking-label"]}>
                Language Of Provider
              </span>{" "}
              - {bookingDetails?.language?.join(", ")}
            </p>
            <p>
              <span className={styles["edt-booking-label"]}>Location</span> -{" "}
              {bookingDetails?.location}
            </p>
            {bookingDetails?.description && (
              <p>
                <span className={styles["edt-booking-label"]}>
                  Additional Instructions
                </span>{" "}
                - {bookingDetails?.description}
              </p>
            )}
          </div>

          {/* Render extra details */}
          {renderExtraDetails()}

          {bookingDetails?.attachment?.length > 0 && (
            <div className={styles["edt-booking-photos"]}>
              <h4 className={styles["edt-booking-photos-title"]}>
                Uploaded Photos
              </h4>
              <div className={styles["edt-booking-photo-gallery"]}>
                {bookingDetails?.attachment.map((item: any, index: number) => (
                  <div key={index}>
                    <img
                      src={URL.createObjectURL(item)}
                      alt={`Uploaded ${index + 1}`}
                      className={styles["edt-booking-photo"]}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-end w-100 d-flex justify-content-end gap-3">
            {/* <Button
              className={styles["edt-booking-nxt-btn"]}
              style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}
              onClick={() => navigate(`/dashboard/create-booking/${bookingDetails?.serviceId || 'view'}?serviceId=${bookingDetails?.serviceId}&serviceName=${encodeURIComponent(bookingDetails?.serviceName)}`, { state: { bookingDetails } })}
            >
              Back to Calendar
            </Button> */}
            <Button
              className={styles["edt-booking-nxt-btn"]}
              onClick={handleNext}
              disabled={isLoading || isUpdating}
            >
              {isLoading || isUpdating ? "Processing..." : (bookingDetails?._id ? "Update Booking" : "Next")}
            </Button>
          </div>
        </div>

        <CompleteProfileModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          message="Please Add Your Phone Number To Continue With Bookings."
        />
      </div>
    </>
  )
}